/**
 * 2D cross-section profiles, in the track's local frame: origin at the rail
 * centreline (heartline − up·HEARTLINE_OFFSET), x = right, y = up. Shared by
 * the renderer (low-res) and the future print exporter (high-res, thickened) —
 * one sweep, two consumers.
 *
 * Cross-section: two side rails joined by a continuous flat beam, with a solid
 * triangle hanging from that beam and an optional gear rack on top.
 */
export interface ProfilePoint {
  x: number;
  y: number;
}

export interface ProfileLoop {
  points: ProfilePoint[]; // CCW, closed implicitly
}

export interface TrackProfileParams {
  gauge: number; // rail centre-to-centre
  railRadius: number;
  /** Lateral width of the gear-rack seating strip on the deck. */
  spineWidth: number;
  /** Vertical height of the continuous flat beam that joins the rails. */
  flatBeamHeight: number;
  /** Top width of the solid triangle where it meets the flat beam. */
  triangleWidth: number;
  /** Vertical height of the solid triangle (flat-beam underside → apex). */
  triangleHeight: number;
}

export const ARROW_PROFILE: TrackProfileParams = {
  gauge: 1.2,
  railRadius: 0.09,
  spineWidth: 0.28,
  flatBeamHeight: 0.08,
  triangleWidth: 0.85,
  triangleHeight: 0.77,
};

/**
 * Thickened Arrow profile for print export (~1.5 mm min feature at 1:100).
 * Render keeps {@link ARROW_PROFILE}; print uses this.
 */
export const PRINT_ARROW_PROFILE: TrackProfileParams = {
  gauge: 1.2,
  railRadius: 0.15,
  spineWidth: 0.35,
  flatBeamHeight: 0.12,
  triangleWidth: 0.85,
  triangleHeight: 0.77,
};

export function circleLoop(cx: number, cy: number, r: number, segments: number): ProfileLoop {
  const points: ProfilePoint[] = [];
  for (let i = 0; i < segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    points.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
  }
  return { points };
}

export function boxLoop(cx: number, cy: number, w: number, h: number): ProfileLoop {
  const hw = w / 2;
  const hh = h / 2;
  return {
    points: [
      { x: cx - hw, y: cy - hh },
      { x: cx + hw, y: cy - hh },
      { x: cx + hw, y: cy + hh },
      { x: cx - hw, y: cy + hh },
    ],
  };
}

/** Solid isosceles triangle, apex down, CCW. */
export function invertedTriangleLoop(halfWidth: number, topY: number, apexY: number): ProfileLoop {
  return {
    points: [
      { x: -halfWidth, y: topY },
      { x: 0, y: apexY },
      { x: halfWidth, y: topY },
    ],
  };
}

/** Top face of the continuous flat beam (gear rack seats here). */
export function rackDeckTopY(_p: TrackProfileParams): number {
  return 0;
}

/** Underside of the continuous flat beam (triangle attaches here). */
export function flatBeamBottomY(p: TrackProfileParams): number {
  return rackDeckTopY(p) - Math.max(p.flatBeamHeight, 1e-4);
}

/** Apex of the solid triangle below the flat beam. */
export function triangleApexY(p: TrackProfileParams): number {
  return flatBeamBottomY(p) - Math.max(p.triangleHeight, 1e-4);
}

/**
 * Depth from rail line to triangle apex — used by supports / print joints.
 * @deprecated Prefer {@link triangleApexY}; kept as a positive depth helper.
 */
export function trackSpineDrop(p: TrackProfileParams): number {
  return -triangleApexY(p);
}

/**
 * Rails + continuous flat beam spanning the gauge + solid triangle hanging
 * from the flat beam underside.
 */
export function trackProfiles(
  p: TrackProfileParams,
  railSegments = 10,
): {
  rails: ProfileLoop[];
  spine: ProfileLoop[];
} {
  const g = p.gauge / 2;
  const flatH = Math.max(p.flatBeamHeight, 1e-4);
  const deckTop = rackDeckTopY(p);
  const deckBottom = flatBeamBottomY(p);
  const deckCy = (deckTop + deckBottom) / 2;
  const apexY = triangleApexY(p);
  const triW = Math.min(Math.max(p.triangleWidth, 1e-3), p.gauge);
  const halfT = triW / 2;

  return {
    rails: [
      circleLoop(-g, 0, p.railRadius, railSegments),
      circleLoop(g, 0, p.railRadius, railSegments),
    ],
    spine: [
      boxLoop(0, deckCy, p.gauge, flatH),
      invertedTriangleLoop(halfT, deckBottom, apexY),
    ],
  };
}

/** Outer envelope radius of the swept track, for support clearance tests. */
export function trackEnvelopeRadius(p: TrackProfileParams): number {
  return Math.max(p.gauge / 2 + p.railRadius, trackSpineDrop(p));
}
